// A guest session is the backend's answer to "who is asking?" for a visitor
// who has not signed in: POST /chat/guest-session hands out a token whose
// claims name a real (guest) customer. The widget takes the customer id from
// those claims. It must not ask /user/info for it, which refuses guests.

const STORAGE_KEY = "meetusvr.guestSession";
const GUEST_DISPLAY_NAME = "Guest";

// A guest is somebody, and saying only "Guest" hides that. The backend gives
// every one of them a number, and since the year-long key shipped that number
// survives a visitor clearing their browser — so "Guest 3003" is the same
// person next week, and the same number the brand sees on their side.
//
// It must be the CUSTOMER number — the token's `userId` claim — and nothing
// else. The chat has a second, internal number of its own (`chatUserId`), and
// naming a guest by that one was a real defect: the chat said "Guest 1856"
// while every card event, order and Pulse record for the same person was filed
// under 3003, so a brand searching its dashboard for the name it had just been
// shown found nobody. One person, one number, and this is the one the rest of
// the business already uses.
const guestDisplayName = (userId) => `${GUEST_DISPLAY_NAME} ${userId}`;
// A token about to expire is not worth restoring: the first request would
// fail and the visitor would see an error instead of a fresh session.
const EXPIRY_MARGIN_SECONDS = 60;

const base64UrlDecode = (segment) => {
  const padded = segment.replace(/-/g, "+").replace(/_/g, "/").padEnd(
    Math.ceil(segment.length / 4) * 4,
    "="
  );
  const binary = atob(padded);
  // atob yields a byte string; the claims are UTF-8. Percent-decoding the
  // bytes works in every browser and in the test runner, unlike TextDecoder.
  const percentEncoded = Array.from(binary, (c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join("");
  return decodeURIComponent(percentEncoded);
};

export const decodeJwtClaims = (token) => {
  if (typeof token !== "string") return {};
  const parts = token.split(".");
  if (parts.length < 2) return {};
  try {
    const claims = JSON.parse(base64UrlDecode(parts[1]));
    return claims && typeof claims === "object" ? claims : {};
  } catch (e) {
    return {};
  }
};

// Whether a token was issued for this shop, according to the token itself.
//
// A saved sign-in is not proof of anything until its own claims agree with the
// shop the panel is embedded for. Two brands served from one domain share one
// browser drawer, so a token found there may belong to the other brand, and
// connecting with it makes this visitor somebody else's customer.
//
// Unknown means no: a token we cannot read, or one that names no shop, is not
// evidence that it belongs here.
export const tokenBelongsToOrg = (token, orgId) => {
  if (!token || orgId === null || orgId === undefined || orgId === "") return false;
  const claims = decodeJwtClaims(token);
  const owner = claims.organizationId ?? claims.orgId;
  if (owner === null || owner === undefined || owner === "") return false;
  return String(owner) === String(orgId);
};

// Whether a token is positively somebody else's.
//
// The mirror of the question above, and deliberately not its opposite. Adopting
// a saved sign-in needs proof that it belongs here, because the cost of being
// wrong is talking to the wrong shop. Refusing one the app already holds needs
// proof that it does NOT, because the cost of being wrong is signing out a
// customer who was fine — and not every token we hold is one we can read.
//
// So a token naming no shop, or one we cannot parse, is left alone here. Only
// a token that names a different shop is refused.
export const tokenNamesAnotherOrg = (token, orgId) => {
  if (!token || orgId === null || orgId === undefined || orgId === "") return false;
  const claims = decodeJwtClaims(token);
  const owner = claims.organizationId ?? claims.orgId;
  if (owner === null || owner === undefined || owner === "") return false;
  return String(owner) !== String(orgId);
};

// The user record the rest of the app expects, built from what the backend
// returned. Null when the token does not identify anyone, so the caller can
// refuse rather than connect a socket nobody can reply to.
export const buildGuestUser = (session) => {
  const claims = decodeJwtClaims(session?.chatToken);
  const id = claims.userId;
  if (!id) return null;
  return {
    id,
    userId: id,
    name: guestDisplayName(id),
    isGuest: true,
    chatUserId: session.chatUserId ?? null,
    guestSessionId: session.guestSessionId ?? null,
    organizationId: claims.organizationId ?? session.organizationId ?? null,
  };
};

const storage = () => {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
};

// The widget runs inside an iframe on the brand's shop, where the tab's storage
// can be blocked outright. The session granted for this page load is therefore
// also held here, so the visitor is still one guest for this visit even when
// nothing can be written down.
let inMemorySession = null;

const readStoredSession = () => {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const isExpired = (saved) => {
  if (!saved.exp) return false;
  return saved.exp - EXPIRY_MARGIN_SECONDS <= Date.now() / 1000;
};

// The conversation is part of the session, not an afterthought.
//
// The backend hands out a chatId alongside the token, and it used to be
// dropped here. Within one page load that went unnoticed: the widget learns
// the id again from the first message that arrives on the socket. Across a
// page load - which is exactly what the shop following the conversation
// causes - there is no first message, so the guest came back with no idea
// which conversation was theirs and was shown the greeting instead of it.
export const saveGuestSession = (orgId, token, user, chatId) => {
  const saved = {
    orgId: String(orgId),
    token,
    user,
    chatId: chatId ?? null,
    exp: decodeJwtClaims(token).exp ?? null,
  };
  inMemorySession = saved;
  const store = storage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch (e) {
    // Storage full or disabled: the session still works for this page load.
  }
};

export const loadGuestSession = (orgId) => {
  const saved = readStoredSession() || inMemorySession;
  if (!saved?.token || !saved?.user || saved.orgId !== String(orgId)) return null;
  if (isExpired(saved)) {
    clearGuestSession();
    return null;
  }
  return { token: saved.token, user: saved.user, chatId: saved.chatId ?? null };
};

export const clearGuestSession = () => {
  inMemorySession = null;
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to clear.
  }
};

// What the handover needs, held for the moment between a guest becoming a
// customer and their conversation being moved onto that customer.
//
// Not on the store: the auth slice is written to localStorage, where this would
// outlive the tab, the sign-out and the visitor. A guest session id with its
// guest token is proof of ownership of a conversation — whatever holds the pair
// can claim that conversation — so it belongs in the same per-tab drawer as the
// guest session it describes, and it is taken out exactly once.
const HANDOVER_KEY = "meetusvr.guestHandover";

let inMemoryHandover = null;

export const rememberGuestHandover = (handover) => {
  inMemoryHandover = handover;
  const store = storage();
  if (!store) return;
  try {
    store.setItem(HANDOVER_KEY, JSON.stringify(handover));
  } catch (e) {
    // Storage full or disabled: it still works for this page load.
  }
};

export const forgetGuestHandover = () => {
  inMemoryHandover = null;
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(HANDOVER_KEY);
  } catch (e) {
    // Nothing to clear.
  }
};

// Read once and remove, whatever the answer. A conversation is claimed on the
// strength of this, so nothing is left behind to be claimed a second time, or
// by somebody else: it is returned only to the account it was captured for.
// The next person to sign in on this browser is not that account, and gets
// nothing rather than a stranger's conversation.
export const takeGuestHandover = (forUserId, orgId) => {
  let saved = inMemoryHandover;
  const store = storage();
  if (!saved && store) {
    try {
      const raw = store.getItem(HANDOVER_KEY);
      saved = raw ? JSON.parse(raw) : null;
    } catch (e) {
      saved = null;
    }
  }
  forgetGuestHandover();

  if (!saved?.guestSessionId || !saved?.guestToken) return null;
  if (!forUserId || String(saved.forUserId) !== String(forUserId)) return null;
  // Two brands served from one domain share this drawer, so the shop is checked
  // as well as the account — see tokenBelongsToOrg above for why that matters.
  // A shop that cannot be named on either side is left alone, as there: the
  // backend checks the brand again anyway, and refusing on an unreadable token
  // would lose the history of a visitor who did nothing wrong.
  if (saved.orgId && orgId && String(saved.orgId) !== String(orgId)) return null;
  return { guestSessionId: saved.guestSessionId, guestToken: saved.guestToken };
};
